import { useTranslation } from "react-i18next";
import { formatMoney } from "@/lib/money";
import type { BookingDiscountBreakdown } from "./sellerBookingRules";

export type BookingPriceBreakdownAudience = "partner" | "admin";

interface BookingPriceBreakdownProps {
  breakdown: BookingDiscountBreakdown;
  /** Customer-paid total when shown (upfront/deposit after promo/points). */
  customerPaidCents?: number | null;
  /** Loyalty points discount from checkout (booking-level, not per line). */
  pointsDiscountCents?: number | null;
  /** DEPOSIT: use deposit paid label + optional venue balance row. */
  isDeposit?: boolean;
  /** DEPOSIT: seller share still due at the venue. */
  balanceDueAtVenueCents?: number | null;
  /** Partner notes say “you”; admin notes describe the partner neutrally. */
  audience?: BookingPriceBreakdownAudience;
  className?: string;
}

function BreakdownRow({
  label,
  value,
  emphasize,
  muted,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={
          muted
            ? "text-sm text-surface-500"
            : emphasize
              ? "text-sm font-medium text-surface-700"
              : "text-sm text-surface-600"
        }
      >
        {label}
      </span>
      <span
        className={
          emphasize
            ? "text-base font-bold tabular-nums text-surface-900"
            : muted
              ? "text-sm tabular-nums text-surface-500"
              : "text-sm font-semibold tabular-nums text-surface-900"
        }
      >
        {value}
      </span>
    </div>
  );
}

/** Partner/admin price stack: list → seller/Flitrip/points discounts → paid. */
export function BookingPriceBreakdown({
  breakdown,
  customerPaidCents,
  pointsDiscountCents,
  isDeposit = false,
  balanceDueAtVenueCents = null,
  audience = "partner",
  className,
}: BookingPriceBreakdownProps) {
  const { t } = useTranslation("bookings");
  const pointsCents =
    pointsDiscountCents != null && pointsDiscountCents > 0
      ? pointsDiscountCents
      : 0;
  const showList =
    breakdown.hasAnyDiscount &&
    breakdown.listTotalCents !== breakdown.serviceTotalCents;
  const showVenueBalance =
    isDeposit &&
    balanceDueAtVenueCents != null &&
    balanceDueAtVenueCents > 0;
  const hasLaterThanService =
    breakdown.hasFlitripDiscount ||
    pointsCents > 0 ||
    customerPaidCents != null ||
    showVenueBalance;
  const paidLabel = isDeposit
    ? t("detail.depositPaid")
    : t("detail.customerPaid");
  const sellerNoteKey =
    audience === "admin"
      ? "adminDetail.payment.sellerDiscountNote"
      : "detail.sellerDiscountNote";
  const flitripNoteKey =
    audience === "admin"
      ? "adminDetail.payment.flitripDiscountNote"
      : "detail.flitripDiscountNote";

  return (
    <div className={className}>
      <div className="space-y-2 rounded-xl bg-surface-50 px-4 py-3.5">
        {showList ? (
          <BreakdownRow
            label={t("detail.listPrice")}
            value={formatMoney(breakdown.listTotalCents)}
            muted
          />
        ) : null}

        {breakdown.hasSellerDiscount ? (
          <BreakdownRow
            label={t("detail.sellerDiscount")}
            value={formatMoney(-breakdown.sellerDiscountCents)}
            muted
          />
        ) : null}

        <BreakdownRow
          label={t("detail.servicePrice")}
          value={formatMoney(breakdown.serviceTotalCents)}
          emphasize={!hasLaterThanService}
        />

        {breakdown.hasFlitripDiscount ? (
          <BreakdownRow
            label={t("detail.flitripDiscount")}
            value={formatMoney(-breakdown.flitripDiscountCents)}
            muted
          />
        ) : null}

        {pointsCents > 0 ? (
          <BreakdownRow
            label={t("detail.pointsDiscount")}
            value={formatMoney(-pointsCents)}
            muted
          />
        ) : null}

        {customerPaidCents != null ? (
          <div className="space-y-2 border-t border-surface-200 pt-2">
            <BreakdownRow
              label={paidLabel}
              value={formatMoney(customerPaidCents)}
              emphasize
            />
            {showVenueBalance ? (
              <BreakdownRow
                label={t("detail.dueAtVenue")}
                value={formatMoney(balanceDueAtVenueCents)}
              />
            ) : null}
          </div>
        ) : showVenueBalance ? (
          <div className="border-t border-surface-200 pt-2">
            <BreakdownRow
              label={t("detail.dueAtVenue")}
              value={formatMoney(balanceDueAtVenueCents)}
              emphasize
            />
          </div>
        ) : null}
      </div>

      {breakdown.hasSellerDiscount ? (
        <p className="mt-2 text-xs leading-relaxed text-surface-500">
          {t(sellerNoteKey)}
        </p>
      ) : null}
      {breakdown.hasFlitripDiscount ? (
        <p className="mt-2 text-xs leading-relaxed text-surface-500">
          {t(flitripNoteKey)}
        </p>
      ) : null}
    </div>
  );
}

export function LineItemDiscountBadges({
  sellerDiscountCents,
  flitripDiscountCents,
}: {
  sellerDiscountCents?: number;
  flitripDiscountCents?: number;
}) {
  const { t } = useTranslation("bookings");
  const badges: string[] = [];
  if ((sellerDiscountCents ?? 0) > 0) {
    badges.push(t("detail.sellerDiscountBadge"));
  }
  if ((flitripDiscountCents ?? 0) > 0) {
    badges.push(t("detail.flitripDiscountBadge"));
  }
  if (badges.length === 0) {
    return null;
  }
  return (
    <p className="mt-1 flex flex-wrap gap-1.5">
      {badges.map((label) => (
        <span
          key={label}
          className="inline-flex rounded-md bg-primary-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700"
        >
          {label}
        </span>
      ))}
    </p>
  );
}
